import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { useResponsive } from '@app/hooks/useResponsive';
import { getNews } from '@app/api/news.api';
import { getBlogPosts } from '@app/api/blog.api';
import { getPages } from '@app/api/pages.api';
import { getUsers } from '@app/api/users.api';
import { getActivityLogs } from '@app/api/activityLogs.api';
import { ActivityLog } from '@app/types/api.types';
import { notificationController } from '@app/controllers/notificationController';
import * as S from './DashboardPage.styles';
import Layout, { Content } from 'antd/lib/layout/layout';

interface StatisticCard {
  title: string;
  value: number;
  icon: string;
  color: string;
  route: string;
}

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();
  const [statistics, setStatistics] = useState<StatisticCard[]>([]);
  const [recentActivities, setRecentActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const [newsRes, blogRes, pagesRes, usersRes, activitiesRes] = await Promise.all([
          getNews({ limit: 1 }),
          getBlogPosts({ limit: 1 }),
          getPages({ limit: 1 }),
          getUsers({ limit: 1 }),
          getActivityLogs({ limit: 10, page: 1 }),
        ]);

        setStatistics([
          {
            title: 'News',
            value: newsRes.total,
            icon: '📰',
            color: '#1890ff',
            route: '/news',
          },
          {
            title: 'Blog Posts',
            value: blogRes.total,
            icon: '📝',
            color: '#52c41a',
            route: '/blog',
          },
          {
            title: 'Pages',
            value: pagesRes.total,
            icon: '📄',
            color: '#faad14',
            route: '/pages',
          },
          {
            title: 'Users',
            value: usersRes.total,
            icon: '👥',
            color: '#f5222d',
            route: '/users',
          },
        ]);

        setRecentActivities(activitiesRes.data || []);
      } catch (error) {
        notificationController.error({ message: 'Failed to load dashboard data' });
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  const formatActivityType = (type: string): string => {
    const types: Record<string, string> = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      login: 'Logged in',
      logout: 'Logged out',
      view: 'Viewed',
    };
    return types[type] || type;
  };

  const formatResourceType = (type: string): string => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };


  return (
    <>
      <PageTitle>Dashboard</PageTitle>
      <Content style={{ padding: '20px' }}>
        <BaseRow gutter={[30, 30]}>
          {/* Statistics Cards */}
          <BaseCol span={24}>
            <BaseRow gutter={[20, 20]}>
              {statistics.map((stat) => (
                <BaseCol key={stat.title} xs={12} sm={12} md={6} xl={6}>
                  <S.StatisticCard
                    onClick={() => navigate(stat.route)}
                    $color={stat.color}
                    loading={loading}
                  >
                    <S.StatisticIcon>{stat.icon}</S.StatisticIcon>
                    <S.StatisticContent>
                      <S.StatisticValue>{stat.value}</S.StatisticValue>
                      <S.StatisticTitle>{stat.title}</S.StatisticTitle>
                    </S.StatisticContent>
                  </S.StatisticCard>
                </BaseCol>
              ))}
            </BaseRow>
          </BaseCol>

          {/* Quick Actions */}
          <BaseCol xs={24} md={24} xl={8}>
            <BaseCard title="Quick Actions" padding="1.5rem">
              <S.QuickActions>
                <BaseButton
                  type="primary"
                  block
                  onClick={() => navigate('/news/create')}
                  style={{ marginBottom: '10px' }}
                >
                  Create News
                </BaseButton>
                <BaseButton
                  type="primary"
                  block
                  onClick={() => navigate('/blog/create')}
                  style={{ marginBottom: '10px' }}
                >
                  Create Blog Post
                </BaseButton>
                <BaseButton
                  type="primary"
                  block
                  onClick={() => navigate('/pages/create')}
                  style={{ marginBottom: '10px' }}
                >
                  Create Page
                </BaseButton>
                <BaseButton
                  type="default"
                  block
                  onClick={() => navigate('/users/create')}
                >
                  Create User
                </BaseButton>
              </S.QuickActions>
            </BaseCard>
          </BaseCol>

          {/* Recent Activities */}
          <BaseCol xs={24} md={24} xl={16}>
            <BaseCard title="Recent Activities" padding="1.5rem">
              {loading ? (
                <div>Loading...</div>
              ) : recentActivities.length === 0 ? (
                <div>No recent activities</div>
              ) : (
                <S.ActivitiesList>
                  {recentActivities.map((activity) => (
                    <S.ActivityItem key={activity.id}>
                      <S.ActivityIcon>
                        {activity.activityType === 'create' && '➕'}
                        {activity.activityType === 'update' && '✏️'}
                        {activity.activityType === 'delete' && '🗑️'}
                        {activity.activityType === 'login' && '🔐'}
                        {activity.activityType === 'logout' && '🚪'}
                        {activity.activityType === 'view' && '👁️'}
                      </S.ActivityIcon>
                      <S.ActivityContent>
                        <S.ActivityDescription>
                          <strong>{activity.userEmail || 'Unknown User'}</strong>{' '}
                          {formatActivityType(activity.activityType)}{' '}
                          {activity.resourceType && (
                            <>
                              {formatResourceType(activity.resourceType)}
                              {activity.resourceId && ` #${activity.resourceId.slice(0, 8)}`}
                            </>
                          )}
                        </S.ActivityDescription>
                        {activity.description && (
                          <S.ActivityMeta>{activity.description}</S.ActivityMeta>
                        )}
                        <S.ActivityDate>{formatDate(activity.createdAt)}</S.ActivityDate>
                      </S.ActivityContent>
                    </S.ActivityItem>
                  ))}
                </S.ActivitiesList>
              )}
            </BaseCard>
          </BaseCol>
        </BaseRow>
      </Content>
    </>
  );
};

export default DashboardPage;

